import { NgClass, NgStyle } from '@angular/common';
import { Component, computed, input, inject } from '@angular/core';
import { MediaObserverService } from '@innogy/shared/responsive-tools';

import type { Hero } from '../models/hero.model';

export interface BackgroundImageStyle {
  backgroundImage?: string;
  backgroundPosition?: string;
  backgroundRepeat?: string;
  backgroundSize?: string;
}

@Component({
  selector: 'wl-hero-wrapper',
  standalone: true,
  imports: [NgStyle, NgClass],
  templateUrl: './hero-wrapper.component.html',
  styleUrls: [
    './hero-wrapper.component.ed.scss',
    './hero-wrapper.component.essent.scss',
  ],
})
export class HeroWrapperComponent {
  readonly #mediaObserverService = inject(MediaObserverService);

  readonly hero = input<Hero>();
  readonly large = input<boolean>();
  readonly borderRadius = input<boolean>();
  readonly hasBreadcrumbs = input<boolean>();
  readonly hasEssentRebranding = input<boolean>();

  readonly ariaLabel = computed(() => {
    const headingLine1 = this.hero()?.headingLine1.value;
    const headingLine2 = this.hero()?.headingLine2?.value;

    return [headingLine1, headingLine2].filter(Boolean).join(' ');
  });

  readonly backgroundImageStyle = computed<BackgroundImageStyle>(() => {
    const { isMobile } = this.#mediaObserverService.deviceInfo();
    const hero = this.hero();
    const desktop = hero?.backgroundImage;
    const mobile = hero?.backgroundImageMobile;
    const useOnMobile = desktop?.useOnMobile;

    const style: BackgroundImageStyle = {};

    const rawSrc = HeroWrapperComponent.getProp(
      mobile?.image?.value?.src,
      desktop?.image?.value?.src,
      isMobile,
      useOnMobile
    );

    const imageUrl = HeroWrapperComponent.getImageUrl(rawSrc);
    if (imageUrl) {
      style.backgroundImage = imageUrl;
    }

    const position = HeroWrapperComponent.getProp(
      mobile?.position,
      desktop?.position,
      isMobile,
      useOnMobile
    );

    if (position) {
      style.backgroundPosition = position;
    }

    const repeat = HeroWrapperComponent.getProp(
      mobile?.repeat,
      desktop?.repeat,
      isMobile,
      useOnMobile
    );

    if (repeat) {
      style.backgroundRepeat = repeat;
    }

    const size = HeroWrapperComponent.getProp(
      mobile?.size,
      desktop?.size,
      isMobile,
      useOnMobile
    );

    if (size) {
      style.backgroundSize = size;
    }

    return style;
  });

  private static getImageUrl(src?: string): string | undefined {
    return src ? `url(${src})` : undefined;
  }

  private static getProp<T>(
    mobileValue: T | undefined,
    desktopValue: T | undefined,
    isMobile: boolean,
    useOnMobile?: boolean
  ): T | undefined {
    if (isMobile && useOnMobile) {
      return mobileValue ?? desktopValue;
    }

    return !isMobile ? desktopValue : undefined;
  }
}
